import React, { useState, useEffect } from 'react';
import { 
  Monitor, 
  Folder, 
  FileText, 
  Music, 
  Image, 
  Terminal, 
  Settings, 
  Palette, 
  Grid3X3,
  Play,
  Download,
  Sparkles,
  ChevronRight,
  X,
  Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import MapleLeaf from '@/components/common/MapleLeaf';

interface WelcomeScreenProps {
  isVisible: boolean;
  onClose: () => void;
  onDontShowAgain: (dontShow: boolean) => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ isVisible, onClose, onDontShowAgain }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowChecked, setDontShowChecked] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const features = [
    {
      icon: <Folder className="w-8 h-8 text-primary" />,
      title: "Advanced File Explorer",
      description: "Navigate with multiple view modes, copy/paste, keyboard shortcuts, and seamless file management",
      highlight: "Multiple Views • Keyboard Shortcuts • Context Menus"
    },
    {
      icon: <FileText className="w-8 h-8 text-blue-500" />,
      title: "Rich Text Editor",
      description: "Create and edit documents with our feature-rich Notepad including save/load functionality",
      highlight: "Auto-Save • File Operations • Clean Interface"
    },
    {
      icon: <Music className="w-8 h-8 text-purple-500" />,
      title: "Music Player",
      description: "Enjoy your audio collection with a beautiful player supporting multiple formats",
      highlight: "Multiple Formats • Playlist Support • Audio Controls"
    },
    {
      icon: <Image className="w-8 h-8 text-green-500" />,
      title: "Image Viewer",
      description: "View your photos and images with zoom, navigation, and slideshow features",
      highlight: "Zoom Controls • Gallery Navigation • Full Screen"
    },
    {
      icon: <Terminal className="w-8 h-8 text-orange-500" />,
      title: "Powerful Terminal",
      description: "Command-line interface with customizable themes, transparency, and modern features",
      highlight: "Custom Themes • Transparency • Modern Commands"
    },
    {
      icon: <Settings className="w-8 h-8 text-red-500" />,
      title: "Control Panel",
      description: "Comprehensive system settings including display properties and system management",
      highlight: "Display Settings • System Reset • Configuration"
    }
  ];

  const systemFeatures = [
    { icon: <Grid3X3 className="w-5 h-5" />, text: "Multiple Desktop Layouts" },
    { icon: <Palette className="w-5 h-5" />, text: "Theme Customization" },
    { icon: <Play className="w-5 h-5" />, text: "Window Management" },
    { icon: <Download className="w-5 h-5" />, text: "App Store Integration" }
  ];

  useEffect(() => {
    if (isVisible) {
      setShowContent(true);
      // Auto-advance slides
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % features.length);
      }, 4000);
      return () => clearInterval(interval);
    } else {
      setShowContent(false);
    }
  }, [isVisible, features.length]);

  const handleClose = () => {
    if (dontShowChecked) {
      onDontShowAgain(true);
    }
    onClose();
  };

  if (!isVisible) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop, not the content
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-black/60 via-black/40 to-black/60 backdrop-blur-md cursor-pointer"
      onClick={handleBackdropClick}
    >
      <div 
        className={`relative bg-gradient-to-br from-card/95 to-card/90 border border-border/50 rounded-3xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden transition-all duration-700 cursor-default ${
          showContent ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-primary/5 rounded-full animate-pulse" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-accent/5 rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/3 left-1/4 w-24 h-24 bg-secondary/3 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-2/3 right-1/4 w-20 h-20 bg-primary/3 rounded-full animate-pulse" style={{ animationDelay: '3s' }} />
        </div>

        {/* Header */}
        <div className="relative p-8 pb-4 text-center border-b border-border/30">
          <div className="absolute top-6 right-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0 hover:bg-white/10 text-muted-foreground hover:text-foreground"
            >
              <X size={16} />
            </Button>
          </div>

          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-2xl flex items-center justify-center animate-bounce" style={{ animationDuration: '2s' }}>
                <MapleLeaf size={32} className="text-primary-foreground" />
              </div>
              <div className="absolute -top-2 -right-2 animate-spin" style={{ animationDuration: '4s' }}>
                <Sparkles size={20} className="text-yellow-400" />
              </div>
            </div>
          </div>

          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent mb-3 animate-fade-in">
            Welcome to MapleOS
          </h1>
          <p className="text-lg text-muted-foreground animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Control your intelligence. Own your horizon.
          </p>
        </div>

        {/* Main content area */}
        <div className="relative p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            
            {/* Feature showcase */}
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-foreground mb-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                Powerful Features
              </h2>
              
              <div className="relative h-64 overflow-hidden rounded-xl bg-gradient-to-br from-muted/20 to-muted/10 border border-border/50">
                {features.map((feature, index) => (
                  <div
                    key={feature.title}
                    className={`absolute inset-0 p-6 transition-all duration-500 ${
                      index === currentSlide 
                        ? 'opacity-100 translate-x-0' 
                        : index < currentSlide 
                        ? 'opacity-0 -translate-x-full' 
                        : 'opacity-0 translate-x-full'
                    }`}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 p-3 bg-gradient-to-br from-white/10 to-white/5 rounded-xl backdrop-blur-sm">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">{feature.title}</h3>
                        <p className="text-muted-foreground mb-3">{feature.description}</p>
                        <div className="flex items-center gap-2 text-sm text-primary">
                          <Check size={16} />
                          <span>{feature.highlight}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Progress indicators */}
                <div className="absolute bottom-4 left-6 flex gap-2">
                  {features.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide ? 'bg-primary w-8' : 'bg-muted-foreground/30'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* System overview */}
            <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <h3 className="text-xl font-semibold text-foreground">System Highlights</h3>
              
              <div className="grid grid-cols-2 gap-4">
                {systemFeatures.map((item, index) => (
                  <div
                    key={item.text}
                    className="p-4 bg-gradient-to-br from-muted/30 to-muted/10 rounded-lg border border-border/30 hover:border-primary/30 transition-all duration-300 animate-scale-in"
                    style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-primary">
                        {item.icon}
                      </div>
                      <span className="text-sm font-medium text-foreground">{item.text}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border/30">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">12+</div>
                  <div className="text-xs text-muted-foreground">Applications</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">∞</div>
                  <div className="text-xs text-muted-foreground">Possibilities</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary">100%</div>
                  <div className="text-xs text-muted-foreground">Retro Cool</div>
                </div>
              </div>

              {/* Call to action */}
              <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20 cursor-pointer" onClick={handleClose}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-foreground">Ready to explore?</h4>
                    <p className="text-sm text-muted-foreground">Click this card or anywhere outside to start your journey</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-primary animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative p-6 pt-4 border-t border-border/30 bg-gradient-to-r from-muted/20 to-muted/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/20 border border-border/30">
              <Checkbox
                id="dontShow"
                checked={dontShowChecked}
                onCheckedChange={(checked) => setDontShowChecked(checked as boolean)}
                className="border-2 border-primary/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground shadow-sm"
              />
              <label htmlFor="dontShow" className="text-sm text-foreground font-medium cursor-pointer select-none">
                Don't show this welcome screen again
              </label>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="border-border/50 hover:border-border"
              >
                Maybe Later
              </Button>
              <Button
                onClick={handleClose}
                className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground font-medium"
              >
                Start Exploring
              </Button>
            </div>
          </div>
        </div>

        {/* Floating elements */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce opacity-20"
              style={{
                left: `${10 + i * 7}%`,
                top: `${15 + (i % 4) * 20}%`,
                animationDelay: `${i * 0.4}s`,
                animationDuration: `${2 + (i % 3)}s`
              }}
            >
              <Sparkles size={8 + (i % 3) * 2} className="text-primary" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};